import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from './common/entity';

export enum FileEntityType {
  pmd = 'pmd',
  company = 'company',
  user = 'user',
  assetTransaction = 'assetTransaction',
  order = 'order',
}

export type FileTypeCategory = 'image' | 'video' | 'document' | 'other';

export interface FilePreview {
  id: string;
  name: string;
  size: string;
  type: string;
  data: string;
}

export class FileEntity extends CompanyEntity {
  name!: string;
  type!: string;
  size!: number;
  entityType!: FileEntityType;
  entityId!: string;
  stockLocationId!: string;
  path!: string;

  forUpdate(): UpdateFileInput {
    return UpdateFileInput.from(this, FileEntity);
  }

  forDelete(): DeleteFileInput {
    return DeleteFileInput.from(this, FileEntity);
  }

  validate(fields: any[]) {}

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
  }

  get primaryFileType() {
    return this.type.split('/')[0];
  }

  get fileTypeCategory() {
    const category = this.primaryFileType;
    if (category === 'image' || category === 'video') return category;

    if (category === 'text' || category === 'application') return 'document';

    return 'other';
  }

  get fileType() {
    return this.type.split('/')[1] || '';
  }
}

export class FileWithSas extends FileEntity {
  signature!: string;
  expirationDate!: Date;

  constructor(obj: any) {
    super(obj);
    Object.assign(this, cloneDeep(obj));
  }
}

export class CreateFileInput extends forCreate(FileEntity) {
  static async fromFile(
    file: File,
    entityId: string,
    entityType: FileEntityType,
    companyId: string,
  ): Promise<CreateFileInput> {
    return new CreateFileInput({
      entityType,
      entityId,
      companyId: companyId,
      name: file.name,
      size: file.size,
      type: file.type,
      data: await FileUtils.fileToBase64(file),
    });
  }
}

export class UpdateFileInput extends forUpdate(FileEntity) {}

export class DeleteFileInput extends forDelete(FileEntity) {}

export namespace FileUtils {
  type FileSizeType = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

  export function calculateFormattedFileSize(size: number): { size: number; sizeType: FileSizeType } {
    if (size < 1024) {
      return { size, sizeType: 'B' };
    }

    if (size < 1024 ** 2) {
      return { size: size / 1024, sizeType: 'KB' };
    }

    if (size < 1024 ** 3) {
      return { size: size / 1024 ** 2, sizeType: 'MB' };
    }

    if (size < 1024 ** 4) {
      return { size: size / 1024 ** 3, sizeType: 'GB' };
    }

    return { size: size / 1024 ** 4, sizeType: 'TB' };
  }

  /**
   *
   * @param size in bytes
   * @param decimalPrecision Number of digits after the decimal point. Must be in the range 0 - 20, inclusive. 0 = integer
   * @returns formatted string representing file size, ex. `265 KB`, `3.6 GB`
   */
  export function fileSizeToFormattedString(size: number, decimalPrecision: number) {
    const sizeProps = calculateFormattedFileSize(size);

    return `${sizeProps.size.toFixed(decimalPrecision)} ${sizeProps.sizeType}`;
  }

  export function getPrimaryFileType(file: File) {
    return file.type.split('/')[0];
  }

  export function getFileTypeCategory(file: File) {
    const category = getPrimaryFileType(file);
    if (category === 'image' || category === 'video') return category;

    if (category === 'text' || category === 'application') return 'document';

    return 'other';
  }

  export function fileToBase64(file: File, options: { prefix: boolean } = { prefix: false }): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (!result) reject('No result');

        if (typeof result === 'string') {
          // Removes the prefix (e.g., "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,")
          resolve(options.prefix ? result : result.split(',')[1]);
        } else {
          reject('Invalid file data');
        }
      };

      reader.onerror = error => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }
}
